<template>
  <div>
    <div class="row" v-if="scrapingServiceLoaded">
      <div class="col-md-12">

        <div class="form-group">
          <div class="form-group">
            <label for="credential-app-name">App</label>
            <select
              v-model="currentCredential.appName"
              :disabled="currentCredential.id"
              class="form-control"
              id="credential-app-name"
            >
              <option v-for="appName in appNames" :value="appName" :key="appName">
                {{ appName }}
              </option>
            </select>
          </div>

          <div class="form-group" v-if="currentCredential.appName">
            <label for="credential-service-name">Serviço</label>
            <select
              v-model="currentCredential.serviceName"
              :disabled="currentCredential.id"
              class="form-control"
              id="credential-service-name"
              @change="clearToken"
            >
              <optgroup v-if="nationalServices.length" label="Nacionais">
                <option v-for="service in nationalServices" :value="service.name" :key="service.name">
                  {{ service.friendlyName }}
                </option>
              </optgroup>
              <optgroup v-if="stateServices.length" label="Demais órgãos">
                <option v-for="service in stateServices" :value="service.name" :key="service.name">
                  {{ service.friendlyName }}
                </option>
              </optgroup>
            </select>
          </div>

          <label for="credential-nickname">Apelido</label>
          <input
            v-model="currentCredential.nickname"
            :disabled="currentCredential.id"
            class="form-control"
            id="credential-nickname"
          >
        </div>

        <div class="form-group">
          <label for="credential-description">Descrição</label>
          <input v-model="currentCredential.description" class="form-control" id="credential-description">
        </div>

        <template v-if="currentCredential.serviceName">
          <div class="form-group"
               v-for="token in tokens()"
               :key="token.field"
          >
            <label :for="`credential-token-${token.field}`">{{ token.label }}
              <span v-if="isOptionalField(token.field)" class="text-muted"></span>
            </label>
            <template v-if="token.mask">
              <input
                v-model="currentCredential.tokens[token.field]"
                :id="`credential-token-${token.field}`"
                class="form-control"
                :class="{ 'is-invalid': getFieldError(token.field) }"
                v-mask="token.mask"
              >
              <small v-if="getFieldError(token.field)" class="text-danger">
                {{ getFieldError(token.field) }}
              </small>
            </template>

            <input
              v-else
              v-model="currentCredential.tokens[token.field]"
              :id="`credential-token-${token.field}`"
              :type="token.type"
              class="form-control"
            >
          </div>
        </template>

        <div class="form-group" v-if="withoutCNPJ">
          <label for="credential-preferential">Preferencial</label>
          <input v-model="currentCredential.preferential" id="credential-preferential" type="checkbox" class="ml-3">
        </div>

        <div class="form-group" v-if="showGovbrLogin">
          <label for="credential-govbr-login">Login GOV.BR</label>
          <input v-model="currentCredential.govbrLogin" id="credential-govbr-login" type="checkbox" class="ml-3">
          <small class="form-text text-muted">
            As consultas de ANTT, ANTT Processos e DNIT deste CNPJ passam a usar esta credencial no lugar do
            certificado digital. O certificado continua valendo para Frotas e SNE.
          </small>
        </div>

      </div>
    </div>

    <div class="row" v-else>
      <div class="col-md-12">
        <h5>Carregando...</h5>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 text-right">
        <button class="btn btn-outline mr-2" @click="$emit('cancel')">Cancelar</button>
        <button class="btn btn-primary" @click="save" :disabled="disabledSaveButton">Salvar</button>
      </div>
    </div>
  </div>
</template>

<script>
  import EntityProxy from '@/models/EntityProxy'
  import GetAccountApps from '@/mixins/GetAccountApps'
  import ModelMapper from '@/mixins/ModelMapper'
  import { isValidCPF } from '@/models/cpf'

  import { mask } from 'vue-the-mask'

  const memoization = {}
  const eligibleAppNames = ['cm-frotas', 'cm-taxas', 'cm-embarcador']
  const credentialFields = ['login', 'cnpj', 'cpf_cnpj', 'cpf', 'password']

  const GOVBR_SERVICES = ['autos_antt', 'autos_antt_processos', 'autos_dnit']

  const optionalFieldsByService = {
    'autos_detran_pe': ['cpf_cnpj'],
    'autos_detran_pe_taxas': ['cpf_cnpj']
  }

  const ufPriorityOrder = [
    'SP', 'MG', 'PR', 'RS', 'RJ', 'BA', 'SC', 'GO', 'MT', 'DF',
    'ES', 'PA', 'MA', 'TO', 'PE', 'RN', 'CE', 'AL', 'MS', 'RR',
    'AM', 'RO', 'PB', 'PI', 'SE', 'AC', 'AP'
  ]

  function servicesSortOrder(a, b) {
    const nameA = (a.friendlyName || '').toUpperCase()
    const nameB = (b.friendlyName || '').toUpperCase()

    const isNationalA = !a.uf
    const isNationalB = !b.uf

    if (isNationalA && isNationalB) {
      const isGovbrA = nameA.includes('GOVBR')
      const isGovbrB = nameB.includes('GOVBR')
      if (isGovbrA !== isGovbrB) return isGovbrA ? -1 : 1

      const isSne20A = nameA.includes('SNE 20')
      const isSne20B = nameB.includes('SNE 20')
      if (isSne20A !== isSne20B) return isSne20A ? -1 : 1

      const isSne40A = nameA.includes('SNE 40')
      const isSne40B = nameB.includes('SNE 40')
      if (isSne40A !== isSne40B) return isSne40A ? -1 : 1

      return nameA.localeCompare(nameB)
    }

    if (isNationalA !== isNationalB) return isNationalA ? -1 : 1

    const ufIndexA = ufPriorityOrder.indexOf(a.uf.toUpperCase())
    const ufIndexB = ufPriorityOrder.indexOf(b.uf.toUpperCase())

    if (ufIndexA !== -1 && ufIndexB !== -1) return ufIndexA - ufIndexB
    if (ufIndexA !== -1) return -1
    if (ufIndexB !== -1) return 1

    return nameA.localeCompare(nameB)
  }

  const isValidCNPJ = (cnpj) => {
    if (!cnpj) return false
    cnpj = cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
    if (cnpj.length !== 14) return false
    if (/^([0-9A-Z])\1+$/.test(cnpj)) return false
    if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpj)) return false

    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    const val = (c) => c.charCodeAt(0) - 48

    let sum = 0
    for (let i = 0; i < 12; i++) sum += val(cnpj[i]) * weights1[i]
    let remainder = sum % 11
    const digit1 = remainder < 2 ? 0 : 11 - remainder
    if (digit1 !== parseInt(cnpj[12], 10)) return false

    sum = 0
    for (let i = 0; i < 13; i++) sum += val(cnpj[i]) * weights2[i]
    remainder = sum % 11
    const digit2 = remainder < 2 ? 0 : 11 - remainder
    return digit2 === parseInt(cnpj[13], 10)
  }

  const isValidCpfCnpj = (value) => {
    if (!value) return false
    const alphanumeric = value.replace(/[^0-9A-Za-z]/g, '')
    if (alphanumeric.length === 14) return isValidCNPJ(alphanumeric)
    const digits = value.replace(/\D/g, '')
    if (digits.length === 11) return isValidCPF(digits)
    return false
  }

  const buildToken = field => {
    if (field === 'cpf')
      return { field, type: 'text', label: 'CPF (login)', mask: '###.###.###-##' }
    if (field === 'cnpj')
      return { field, type: 'text', label: 'CNPJ', mask: 'XX.XXX.XXX/XXXX-##' }
    if (field === 'cpf_cnpj')
      return { field, type: 'text', label: 'CPF/CNPJ (veículo)', mask: ['###.###.###-##', 'XX.XXX.XXX/XXXX-##'] }
    if (field === 'login')
      return { field, type: 'text', label: 'Login'}
    if (field === 'password')
      return { field, type: 'password', label: 'Senha'}
  }

  const handleTokens = tokens => {
    const newToken = {}
    Object.entries(tokens).forEach(([key, value]) => {
      if (key === 'password') {
        if (value) newToken[key] = value
      } else if (key === 'cpf') {
        newToken[key] = value ? value.replace(/\D/g, '') : ''
      } else if (['cnpj', 'cpf_cnpj'].includes(key)) {
        newToken[key] = value ? value.replace(/[^0-9A-Za-z]/g, '') : ''
      } else {
        newToken[key] = value
      }
    })

    return newToken
  }

  export default {
    components: { },
    computed: {
      showGovbrLogin() {
        return GOVBR_SERVICES.includes(this.currentCredential.serviceName)
      },
      disabledSaveButton() {
        const tokens = this.tokens()
        const serviceName = this.currentCredential.serviceName
        const optionalFields = optionalFieldsByService[serviceName] || []

        const requiredTokens = tokens.filter(token => !optionalFields.includes(token.field))
        const allRequiredFieldsFilled = requiredTokens.every(token => this.currentCredential.tokens[token.field])

        // Validação CPF/CNPJ combinado
        const cpfCnpjValue = this.currentCredential.tokens?.cpf_cnpj
        const hasCpfCnpjField = tokens.some(token => token.field === 'cpf_cnpj')
        const isCpfCnpjOptional = optionalFields.includes('cpf_cnpj')
        const cpfCnpjValid = !hasCpfCnpjField || (isCpfCnpjOptional && !cpfCnpjValue) || isValidCpfCnpj(cpfCnpjValue)

        // Validação CPF separado
        const cpfValue = this.currentCredential.tokens?.cpf
        const hasCpfField = tokens.some(token => token.field === 'cpf')
        const cpfValid = !hasCpfField || !cpfValue || isValidCPF(cpfValue)

        // Validação CNPJ separado
        const cnpjValue = this.currentCredential.tokens?.cnpj
        const hasCnpjField = tokens.some(token => token.field === 'cnpj')
        const cnpjValid = !hasCnpjField || !cnpjValue || isValidCNPJ(cnpjValue)

        return !this.currentCredential.nickname ||
               !this.currentCredential.serviceName ||
               !allRequiredFieldsFilled ||
               !cpfCnpjValid ||
               !cpfValid ||
               !cnpjValid
      },
      cpfCnpjError() {
        const tokens = this.tokens()
        const hasCpfCnpjField = tokens.some(token => token.field === 'cpf_cnpj')
        if (!hasCpfCnpjField) return null

        const cpfCnpjValue = this.currentCredential.tokens?.cpf_cnpj
        if (!cpfCnpjValue) return null

        const cleaned = cpfCnpjValue.replace(/\D/g, '')
        if (cleaned.length === 0) return null
        if (cleaned.length < 11) return null
        if (cleaned.length === 11 && !isValidCPF(cleaned)) return 'CPF inválido'
        if (cleaned.length > 11 && cleaned.length < 14) return null
        if (cleaned.length === 14 && !isValidCNPJ(cleaned)) return 'CNPJ inválido'

        return null
      },
      cpfError() {
        const tokens = this.tokens()
        const hasCpfField = tokens.some(token => token.field === 'cpf')
        if (!hasCpfField) return null

        const cpfValue = this.currentCredential.tokens?.cpf
        if (!cpfValue) return null

        const cleaned = cpfValue.replace(/\D/g, '')
        if (cleaned.length < 11) return null
        if (!isValidCPF(cleaned)) return 'CPF inválido'

        return null
      },
      cnpjError() {
        const tokens = this.tokens()
        const hasCnpjField = tokens.some(token => token.field === 'cnpj')
        if (!hasCnpjField) return null

        const cnpjValue = this.currentCredential.tokens?.cnpj
        if (!cnpjValue) return null

        const cleaned = cnpjValue.replace(/[^0-9A-Za-z]/g, '')
        if (cleaned.length < 14) return null
        if (!isValidCNPJ(cleaned)) return 'CNPJ inválido'

        return null
      },
      appNames() {
        return this.getAccountApps.filter(aa => eligibleAppNames.includes(aa.name))
          .map(aa => aa.name)
      },
      servicesWithCredential() {
        return this.scrapingServices.filter(service => {
          if (service.validateFields.includes('certificate_base64') && !this.scrapingServices.scraper_name ===! 'autos_sne_denatran')
            return false

          return service.appName === this.currentCredential.appName &&
            service.validateFields.some(field => credentialFields.includes(field))
        }).sort(servicesSortOrder)
      },
      nationalServices() {
        return this.servicesWithCredential.filter(s => !s.uf)
      },
      stateServices() {
        return this.servicesWithCredential.filter(s => !!s.uf)
      },
      withoutCNPJ() {
        const currentScrapingService = this.scrapingServices.find(ss => ss.name === this.currentCredential.serviceName)
        if (currentScrapingService && currentScrapingService.name == 'autos_antt')
          return true
        if (currentScrapingService)
          return !currentScrapingService.validateFields.includes('cnpj')
        else
          return true
      }
    },
    created() {
      if (this.credential)
        this.currentCredential = EntityProxy.clone(this.credential)
      else
        this.currentCredential = EntityProxy.build({app_name: eligibleAppNames[0], tokens: {}})

      this.fetchScrapingServices()
    },
    data: () => ({
      currentCredential: {},
      nameModel: 'Credential',
      scrapingServices: [],
      scrapingServiceLoaded: false
    }),
    directives: { mask },
    methods: {
      fetchScrapingServices() {
        const key = 'scrapingServiceToCredential'
        const data = this.$ls.get(key)

        if (data) {
          this.scrapingServices = EntityProxy.build(data)
          this.scrapingServiceLoaded = true
          return
        }

        const params = {
          meta: false,
          per_page: 10000,
          query: {
            app_name: eligibleAppNames
          }
        }

        return this.axios.get('maestro/scraping_services', { params })
          .then(response => this.scrapingServices = EntityProxy.build(response.data))
          .then(() => this.$ls.set(key, this.scrapingServices, { minutes: 1 }))
          .then(() => this.scrapingServiceLoaded = true)
          .catch(e => this.$notify.addError(e))
      },
      save() {
        const credential = EntityProxy.clone(this.currentCredential)
        credential.tokens = handleTokens(credential.tokens)

        this.asyncTemplateSave(credential, {
          after: ({model}) => {
            this.$notify.add('Credencial salva com Sucesso!')
            this.$emit('saved', model)
            this.currentCredential = model
          }
        })
      },
      clearToken() {
        this.currentCredential.tokens = {}
      },
      isOptionalField(fieldName) {
        const serviceName = this.currentCredential.serviceName
        const optionalFields = optionalFieldsByService[serviceName] || []
        return optionalFields.includes(fieldName)
      },
      tokens() {
        const serviceName = this.currentCredential.serviceName
        const appName = this.currentCredential.appName
        const key = `${serviceName}-${appName}`
        if (memoization[key])
          return memoization[key]

        const scrapingService = this.scrapingServices.find(ss => ss.name === serviceName && ss.appName === appName)
        const validateFields = scrapingService ? scrapingService.validateFields : []

        const tokens = credentialFields.filter(field => validateFields.includes(field))
                                       .map(field => buildToken(field))

        if (tokens.length > 0)
          memoization[key] = tokens

        return tokens
      },
      getFieldError(field) {
        if (field === 'cpf_cnpj') return this.cpfCnpjError
        if (field === 'cpf') return this.cpfError
        if (field === 'cnpj') return this.cnpjError
        return null
      },
    },
    mixins: [GetAccountApps, ModelMapper],
    name: 'FormCredential',
    props: {
      credential: { default: null, type: Object },
    }
  }
</script>

<style scoped>
  .btn {
    min-width: 134px;
  }

  p {
    color: black;
  }

  label {
    color: var(--primary-text-color);
  }

  .form-control {
    color: var(--input-text-color);
    background-color: var(--input-bg);
    border-color: var(--input-border);
  }

  .form-control:disabled {
    background-color: var(--input-disabled-bg, #e9ecef);
    color: var(--input-disabled-text, #6c757d);
    opacity: 1;
  }

  h5 {
    color: var(--primary-text-color);
  }

</style>
